import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Table } from '@tanstack/react-table';
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from 'lucide-react';

interface DataTablePaginationProps<TData> {
  table: Table<TData>;
  // External pagination props
  externalPagination?: {
    page: number;
    pageSize: number;
    totalPages: number;
    total: number;
    hasNext: boolean;
    hasPrev: boolean;
    onPageChange: (page: number) => void;
    onPageSizeChange: (pageSize: number) => void;
  };
}

export function DataTablePagination<TData>({
  table,
  externalPagination,
}: DataTablePaginationProps<TData>) {
  // Use external pagination if provided, otherwise use table's built-in pagination
  const isExternalPagination = !!externalPagination;

  const currentPage = isExternalPagination
    ? externalPagination.page
    : table.getState().pagination.pageIndex + 1;

  const pageSize = isExternalPagination
    ? externalPagination.pageSize
    : table.getState().pagination.pageSize;

  const totalPages = isExternalPagination
    ? externalPagination.totalPages
    : table.getPageCount();

  const totalRows = isExternalPagination
    ? externalPagination.total
    : table.getFilteredRowModel().rows.length;

  const canPreviousPage = isExternalPagination
    ? externalPagination.hasPrev
    : table.getCanPreviousPage();

  const canNextPage = isExternalPagination
    ? externalPagination.hasNext
    : table.getCanNextPage();

  const handlePageSizeChange = (value: string) => {
    const newPageSize = Number(value);
    if (isExternalPagination) {
      externalPagination.onPageSizeChange(newPageSize);
    } else {
      table.setPageSize(newPageSize);
    }
  };

  const handleFirstPage = () => {
    if (isExternalPagination) {
      externalPagination.onPageChange(1);
    } else {
      table.setPageIndex(0);
    }
  };

  const handlePreviousPage = () => {
    if (isExternalPagination) {
      externalPagination.onPageChange(currentPage - 1);
    } else {
      table.previousPage();
    }
  };

  const handleNextPage = () => {
    if (isExternalPagination) {
      externalPagination.onPageChange(currentPage + 1);
    } else {
      table.nextPage();
    }
  };

  const handleLastPage = () => {
    if (isExternalPagination) {
      externalPagination.onPageChange(totalPages);
    } else {
      table.setPageIndex(table.getPageCount() - 1);
    }
  };

  return (
    <div className="flex items-center justify-between px-2 w-full">
      <div className="text-muted-foreground flex-1 text-sm">
        {`${table.getFilteredSelectedRowModel().rows.length} of ${totalRows} row(s) selected.`}
      </div>
      <div className="flex items-center space-x-6 lg:space-x-8">
        <div className="flex items-center space-x-2">
          <p className="text-sm font-medium">Rows per page</p>
          <Select value={`${pageSize}`} onValueChange={handlePageSizeChange}>
            <SelectTrigger className="h-8 w-[70px]">
              <SelectValue placeholder={pageSize} />
            </SelectTrigger>
            <SelectContent side="top">
              {[10, 20, 25, 30, 40, 50].map(pageSize => (
                <SelectItem key={pageSize} value={`${pageSize}`}>
                  {pageSize}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex w-[100px] items-center justify-center text-sm font-medium">
          {`Page ${currentPage} of ${totalPages}`}
        </div>
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="icon"
            className="hidden size-8 lg:flex"
            onClick={handleFirstPage}
            disabled={!canPreviousPage}
          >
            <span className="sr-only">Go to first page</span>
            <ChevronsLeft />
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="size-8"
            onClick={handlePreviousPage}
            disabled={!canPreviousPage}
          >
            <span className="sr-only">Go to previous page</span>
            <ChevronLeft />
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="size-8"
            onClick={handleNextPage}
            disabled={!canNextPage}
          >
            <span className="sr-only">Go to next page</span>
            <ChevronRight />
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="hidden size-8 lg:flex"
            onClick={handleLastPage}
            disabled={!canNextPage}
          >
            <span className="sr-only">Go to last page</span>
            <ChevronsRight />
          </Button>
        </div>
      </div>
    </div>
  );
}
